import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../lib/api'
import { useAuthStore } from '../store/auth'
import { track, identify, reset } from '../lib/analytics'
import type { User } from '@fellowship/shared'

interface AuthPayload {
  user: User
  accessToken: string
  refreshToken: string
}

// ─── Register ─────────────────────────────────────────────────────────────────

export function useRegister() {
  const { setTokens } = useAuthStore()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: { email: string; password: string; displayName: string }) =>
      apiFetch<{ data: AuthPayload }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: ({ data }) => {
      setTokens(data.accessToken, data.refreshToken, data.user)
      identify(data.user.id, { email: data.user.email, role: data.user.role })
      track('sign_up', { method: 'email' })
      navigate('/onboarding')
    },
  })
}

// ─── Login ────────────────────────────────────────────────────────────────────

export function useLogin() {
  const { setTokens, setOnboardingComplete } = useAuthStore()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      apiFetch<{ data: AuthPayload }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: ({ data }) => {
      setTokens(data.accessToken, data.refreshToken, data.user)
      identify(data.user.id, { email: data.user.email, role: data.user.role })
      if (data.user.locationLat) setOnboardingComplete()
      if (data.user.role === 'super_admin') {
        navigate('/admin/users')
      } else if (!data.user.locationLat) {
        navigate('/onboarding')
      } else if (data.user.churchSlug) {
        navigate(`/churches/${data.user.churchSlug}/dashboard`)
      } else {
        navigate('/app')
      }
    },
  })
}

// ─── Logout ───────────────────────────────────────────────────────────────────

export function useLogout() {
  const { refreshToken, clearAuth } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      apiFetch('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),
    onSettled: () => {
      reset()
      clearAuth()
      queryClient.clear()
      navigate('/login')
    },
  })
}

// ─── Forgot password ──────────────────────────────────────────────────────────

export function useForgotPassword() {
  return useMutation({
    mutationFn: (data: { email: string }) =>
      apiFetch('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

// ─── Reset password ───────────────────────────────────────────────────────────

export function useResetPassword() {
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: { token: string; password: string }) =>
      apiFetch('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      navigate('/login?reset=1')
    },
  })
}

// ─── Verify email ─────────────────────────────────────────────────────────────

export function useVerifyEmail() {
  const { setUser } = useAuthStore()

  return useMutation({
    mutationFn: (token: string) =>
      apiFetch<{ data: { user: User } }>('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    onSuccess: ({ data }) => {
      setUser(data.user)
    },
  })
}

// ─── Current user ─────────────────────────────────────────────────────────────

export function useMe() {
  const { accessToken, setUser } = useAuthStore()

  return useQuery({
    queryKey: ['me'],
    queryFn: () =>
      apiFetch<{ data: User }>('/users/me').then(({ data }) => {
        setUser(data)
        return data
      }),
    enabled: Boolean(accessToken),
    staleTime: 5 * 60 * 1000,
  })
}

// ─── Update profile / location ────────────────────────────────────────────────

export function useUpdateMe() {
  const { setUser } = useAuthStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Partial<Pick<User, 'displayName' | 'locationLat' | 'locationLng' | 'locationLabel' | 'notifyNewEvents' | 'denomination' | 'privacyShowLastName' | 'privacyRsvpPublic'>>) =>
      apiFetch<{ data: User }>('/users/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: ({ data }) => {
      setUser(data)
      void queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })
}
